import {
  Button,
  Col,
  Divider,
  Flex,
  Image,
  Row,
  Space,
  Tabs,
  TabsProps,
  Typography,
} from "antd";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import ClipLoader from "react-spinners/ClipLoader";

// OPTIMIZED: Replaced @ant-design/icons with lightweight SVGs
import { ArrowLeftIcon, PhoneIcon } from "../../../components/Icons";


// import { turfData } from "../../../config/turf.config";
import { TileProps } from "../../Interfaces";
// import CarouselModel from "../../../components/ImagesCarousel";
// import TurfInfoList from "../TileContainer/TurfInfoList";
// import { getSubdomain } from "../../../utils/getSubDomain";
import constants from "../../../utils/constants";
import MapComponent from "../../../components/Map/Index";
import OverviewComponent from "../../../components/DetailPageComponent/Overview";
import HelmetComponent from "../../../components/Helmet/HelmetComponent";
import {showBookNowDynamic } from "../../../utils/showFeatures";
import { useCities } from "../../../contexts/CitiesContext";
import ShareButton from "../../../components/ShareButton/index";
import {
  GET_TURFS_BY_USERNAME,
} from '../../../utils/queries';
import { transformSingleTurfData } from "../../../utils/transformSingleTurfData";
import { useTurfContext } from "../../../contexts/TurfContext";
import { areaNameToSlug, buildTurfBookingRoute } from "../../../utils/turfRoutes";
import {
  deriveVenueSports,
  filterPitchesBySport,
  formatSportLabel,
  getPitchSports,
  isSportEnabledAtTurf,
} from "../../../utils/sport";
import {
  getPrimaryVisiblePitch,
  isBookablePublicTurf,
} from "../../../utils/turfVisibility";
import {
  generateTurfPageTitle,
  generateTurfMetaDescription,
  generateTurfKeywords,
  getTurfCanonicalUrl,
  generateTurfStructuredData,
  generateTurfBreadcrumbSchema,
  TurfSEOInput,
} from "../../../utils/seoHelpers";
import { formatCityName, getCanonicalCitySlug } from "../../../config/cityData";
import { getPitchFullSpecificationLabel } from "../../../utils/pitchSpecification";


// import TabComponent from "../../../components/tab";

interface AreaPrice {
  area: string;
  playingFormat?: string | null;
  pitchDimensions: {
    length: number | null;
    width: number | null;
    height: number | null;
  };
  pitchName:string;
  sport?: string;

  price: {
    weekday: {
      regularHours: number;
      happyHours: {
        range: string;
        price: number;
      };
    };
    weekend: {
      regularHours: number;
      happyHours: {
        range: string;
        price: number;
      };
    };
  };
}

const turfObj = {
  name: "",
  imageUrl: [],
  fields: [],
  data: [],
  bannerImageUrl: "",
  userName: "",
};


function SingleTileContainer({
  name,
  city,
  pitchname,
  areaName,
  isLoading,
  initialTurfData,
}: {
  name: string | null;
  city: string;
  pitchname?: string;
  areaName?: string | null;
  isLoading?: boolean;
  initialTurfData?: TileProps | null;
}) {
  const { cities } = useCities();
  const { Title, Text } = Typography;

  // const carouselRef = useRef<HTMLDivElement>(null);
  // const [activeMenu, setActiveMenu] = useState<string | null>("overview");
  let arr: any[] = [];
  arr.length = 10;

  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const router = useRouter();
  const [turf, setTurf] = useState<TileProps>(() =>
    initialTurfData || {
      ...turfObj,
      imageUrl: [],
      fields: [],
      data: {},
    }
  );
  const [isDataLoading, setIsDataLoading] = useState(!initialTurfData);

  const [matchedUsernamePrice, setMatchedUsernamePrice] = useState<number>(0);
  const area = turf.data.areaPrice?.[0]?.area;
 
  
  const areaPriceObj = turf?.data?.areaPrice || [];
  // const price = areaPriceObj[0]?.price?.weekday?.regularHours;
  const isRainProtected = turf?.data?.isRainProtected;
  const hasCafe = turf?.data?.hasCafe;

  const [selectedPitchName, setSelectedPitchName] = useState<string>('');
  const routeAreaName = areaName || turf?.data?.areaName || null;
  const { setCurrentTurfLogo } = useTurfContext();

  const [selectedSport, setSelectedSport] = useState<string>('');
  const supportedSports: string[] = useMemo(
    () =>
      deriveVenueSports({
        turfSupportedSports: turf?.data?.supportedSports,
        pitches: areaPriceObj,
      }),
    [turf?.data?.supportedSports, areaPriceObj]
  );
  const isSelectedSportClosed =
    !!selectedSport &&
    !isSportEnabledAtTurf(selectedSport, turf?.data?.enabledSports);

  // Read sport from URL query param (passed from listing page)
  const querySport = (router.query.sport as string) || '';

  // Filter pitches by selected sport - check supportedSports array
  const filteredAreaPriceObj = useMemo(
    () => filterPitchesBySport(areaPriceObj, selectedSport),
    [areaPriceObj, selectedSport]
  );

  // Get the starting price for selected sport's pitches - use basePrice only
  const price = useMemo(() => {
    if (filteredAreaPriceObj.length > 0) {
      const firstPitch = filteredAreaPriceObj[0];
      const basePrice = firstPitch?.basePrice || 0;
      
      if (basePrice > 0) return basePrice;
      
      // Fallback: try daily pricing minimum
      if (firstPitch?.price?.length > 0) {
        const prices = firstPitch.price.map((item: any) => item.price).filter((p: number) => p > 0);
        if (prices.length > 0) return Math.min(...prices);
      }
    }
    return 0;
  }, [filteredAreaPriceObj]);

  // Set the initial sport from the query or requested pitch before falling
  // back to the venue's first sport.
  useEffect(() => {
    if (supportedSports.length > 0 && !selectedSport) {
      if (querySport && supportedSports.includes(querySport)) {
        setSelectedSport(querySport);
      } else {
        const requestedPitch = areaPriceObj.find(
          (pitch: AreaPrice) => pitch.pitchName === pitchname
        );
        const requestedSport = getPitchSports(requestedPitch)[0];
        setSelectedSport(
          requestedSport && supportedSports.includes(requestedSport)
            ? requestedSport
            : supportedSports[0]
        );
      }
    }
  }, [supportedSports, querySport, areaPriceObj, pitchname, selectedSport]);

  useEffect(() => {
    if (!selectedSport || !areaPriceObj.length) return;
    
    const filtered = filterPitchesBySport(areaPriceObj, selectedSport);
    
    if (filtered.length > 0) {
      const requestedPitchIndex = filtered.findIndex(
        (pitch: AreaPrice) => pitch.pitchName === pitchname
      );
      const selectedIndex = requestedPitchIndex >= 0 ? requestedPitchIndex : 0;
      setSelectedPitchName(filtered[selectedIndex].pitchName);
      setSelectedItem(filtered[selectedIndex]);
      setSelected(selectedIndex);
    }
  }, [selectedSport, areaPriceObj, pitchname]);
   
  const [pitchName, setPitchName] = useState('');
  const [turfNotFound, setTurfNotFound] = useState(false);
  
 

  // Clean up when component unmounts
  useEffect(() => {
    return () => {
      setCurrentTurfLogo(null);
    };
  }, [setCurrentTurfLogo]);

  const TURF_URL = process.env.NEXT_PUBLIC_TURF_URL;

  // Show loading state while data is being fetched
  // if (isDataLoading) {
  //   return (
  //     <div className="loader-container">
  //       <ClipLoader size={50} color="purple" loading={true} />
  //       <p>Loading turf details...</p>
  //     </div>
  //   );
  // }

  // Use a ref to track the last fetched username to prevent duplicate fetches
  const lastFetchedUsernameRef = useRef<string | null>(null);

  useEffect(() => {
    if (!initialTurfData?.userName) return;
    setTurf(initialTurfData);
    setIsDataLoading(false);
    setCurrentTurfLogo(initialTurfData.logo || null);
  }, [initialTurfData, setCurrentTurfLogo]);

  useEffect(() => {
    // Reset state when route changes
    if (!name) {
      setTurfNotFound(true);
      router.push(`/noData`);
      return;
    }

    // Create a unique key from name + city + pitchname to detect route changes
    const routeKey = `${name}-${city}-${pitchname || ''}`;

    if (
      initialTurfData?.userName &&
      initialTurfData.userName === name &&
      initialTurfData.fields?.length > 0
    ) {
      lastFetchedUsernameRef.current = routeKey;
      setIsDataLoading(false);
      return;
    }
    
    // Skip if we already fetched for this exact route (prevents duplicate calls during same route)
    if (lastFetchedUsernameRef.current === routeKey) {
      return;
    }


    // Reset loading and turf state when props change
    setIsDataLoading(true);
    setTurf({
      ...turfObj,
      imageUrl: [],
      fields: [],
      data: {},
    });
    setTurfNotFound(false);
    lastFetchedUsernameRef.current = routeKey;

    const fetchTurfDetails = async () => {
      try {
        const TURF_URL = process.env.NEXT_PUBLIC_TURF_URL;
        if (!TURF_URL) {
          console.error("TURF_URL not configured");
          setIsDataLoading(false);
          return;
        }


        const response = await fetch(TURF_URL + `/graphql`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query: GET_TURFS_BY_USERNAME,
            variables: { username: name, input: null },
          }),
        });

        const result = await response.json();

        if (isBookablePublicTurf(result?.data?.turfByUsername)) {
          const transformedData = transformSingleTurfData(
            result?.data?.turfByUsername,
            setMatchedUsernamePrice,
            formatCityName(city)
          );
          setTurf(transformedData);
          // Set the logo from GraphQL API data - use first image as logo if available
          const logoFromAPI = transformedData?.logo || null;
          setCurrentTurfLogo(logoFromAPI);
          setIsDataLoading(false);
        } else {
          console.error("❌ No turf data found:", result.errors);
          setTurfNotFound(true);
          setIsDataLoading(false);
          router.push(`/noData`);
        }
      } catch (error) {
        console.error("❌ Error fetching turf details:", error);
        setTurfNotFound(true);
        setIsDataLoading(false);
        router.push(`/noData`);
      }
    };

    fetchTurfDetails();
  }, [name, city, pitchname, router, initialTurfData]);

  const goBack = () => {
    // Always navigate to city homepage instead of browser back
    // This prevents navigation loops between turf details and booking
    router.push(`/${city}`);
  };

  const handleBookNow = () => {
    if (isSelectedSportClosed) return;
    // When a sport is selected, use the first pitch of that sport (selectedPitchName)
    // Only use URL's pitchname if no sport is selected or it matches the current sport
    let finalPitchName = selectedPitchName;
    if (pitchname && !selectedSport) {
      finalPitchName = pitchname;
    }
    if (!name || !finalPitchName) return;
    const bookingUrl = buildTurfBookingRoute({
      city,
      userName: name,
      pitchName: finalPitchName,
      areaName: routeAreaName,
    });
    router.push(selectedSport ? `${bookingUrl}?sport=${selectedSport}` : bookingUrl);
  };

  // const handleScrollLeft = () => {
  //   if (carouselRef.current) {
  //     carouselRef.current.scrollLeft -= 200;
  //   }
  // };

  // const handleScrollRight = () => {
  //   if (carouselRef.current) {
  //     carouselRef.current.scrollLeft += 200;
  //   }
  // };

  // const handleScroll = (id: string) => {
  //   const targetHeading = document.getElementById(id);
  //   setActiveMenu(id);
  //   if (targetHeading) {
  //     targetHeading.scrollIntoView({ behavior: "smooth" });
  //   }
  // };
  const imgMeta = `${turf.name} | ${turf?.data?.city} | TurfBooking`;

  const actualCitySlug = turf?.data?.city
    ? getCanonicalCitySlug(turf.data.city)
    : city;
  const primaryPitchName =
    getPrimaryVisiblePitch<AreaPrice>(areaPriceObj)?.pitchName || pitchname || "";

  const seoInput: TurfSEOInput | null = turf?.name ? {
    turfName: turf.name,
    username: name || '',
    pitchName: primaryPitchName,
    citySlug: actualCitySlug,
    areaName: routeAreaName,
    address: turf?.data?.address,
    price: matchedUsernamePrice || price,
    imageUrl: turf?.imageUrl?.[0] || turf?.logo,
    latitude: turf?.data?.location?.latitude,
    longitude: turf?.data?.location?.longitude,
    phone: turf?.data?.actualContact,
    amenities: [
      ...(turf?.data?.isRainProtected ? ['Rain Protected'] : []),
      ...(turf?.data?.hasCafe ? ['Cafe Available'] : []),
    ],
  } : null;

  const [selectedArea, setSelectedArea] = useState(area);
 
  const [selectedItem, setSelectedItem] = useState(areaPriceObj[0]);
  const [selected, setSelected] = useState(0);
 
  useEffect(()=>{
    if(selectedItem){
      setPitchName(selectedItem.pitchName);
    }
  },[selectedItem]);

  useEffect(() => {
    const matchingAreaIndex = filteredAreaPriceObj.findIndex(
      (item: AreaPrice) => item.area === area
    );
    if (matchingAreaIndex !== -1) {
      setSelectedArea(filteredAreaPriceObj[matchingAreaIndex].area);
      setSelectedItem(filteredAreaPriceObj[matchingAreaIndex]);
      setSelected(matchingAreaIndex);
    } else if (filteredAreaPriceObj.length > 0) {
      setSelectedItem(filteredAreaPriceObj[0]);
      setSelected(0);
    }
  }, [area, filteredAreaPriceObj]);

  const handleArea = (item: AreaPrice) => {
    setSelectedItem(item);
  };

  const getGalleryImages = (): string[] => {
    if (!selectedSport) {
      return turf?.imageUrl || [];
    }
    const sportImagesRaw = turf?.data?.sportImages;
    if (!sportImagesRaw) return turf?.imageUrl || [];
    try {
      const parsed: Record<string, string[]> =
        typeof sportImagesRaw === 'string' ? JSON.parse(sportImagesRaw) : sportImagesRaw;
      const sportImages = parsed[selectedSport];
      if (sportImages && sportImages.length > 0) return sportImages;
      return turf?.imageUrl || [];
    } catch {
      return turf?.imageUrl || [];
    }
  };

  const items: TabsProps["items"] = [
    {
      key: "overview",
      label: <Title level={5}>Overview</Title>,
      children: (
        <Flex vertical gap={24}>
          <Flex vertical gap={2}>
            <Title level={2}>Overview</Title>
            <OverviewComponent areaPrice={filteredAreaPriceObj} />
          </Flex>
          <Row gutter={[16, 16]} justify={"space-between"}>
            <Col md={8} sm={24} xs={24}>
              <Flex vertical gap={8} className="flex-cards">
                <Title level={3}>Pricing</Title>

                {filteredAreaPriceObj.length === 0 ? (
                  <Text type="secondary">No pitches configured for {selectedSport ? formatSportLabel(selectedSport) : 'this sport'} yet. Please add pitches from the admin panel.</Text>
                ) : (
                <>
                {/* Show header text based on sport type */}
                {filteredAreaPriceObj.some((item: AreaPrice) => item.pitchDimensions) && (
                  <Text strong={isMobile}>
                    {filteredAreaPriceObj.some(
                      (item: AreaPrice) => item.playingFormat || Number(item.area) > 0
                    )
                      ? 'Pitch options - '
                      : 'Court options - '}
                  </Text>
                )}

                <Flex vertical gap={4} style={{ flexDirection: "column" }}>
                  {filteredAreaPriceObj.map(
                    (item: AreaPrice, i: number) =>
                      item.pitchDimensions && (
                        <div className="area-container" key={i}>
                          <div className="turf-area-options">
                            <Button
                              style={{
                                padding: "0.1rem 0.5rem",
                                height: "fit-content",
                                backgroundColor:
                                  i === selected ? "purple" : "white",
                                color: i === selected ? "white" : "black",
                                border: "1px solid black",
                                fontSize: isMobile ? "10px" : undefined,
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelected(i);
                                setSelectedPitchName(item.pitchName);
                                handleArea(item);
                              }}
                              type={i === 0 ? "primary" : "default"}
                              shape="round"
                            >
                              {getPitchFullSpecificationLabel(item, i)}
                            </Button>
                          </div>
                        </div>
                      )
                  )}
                </Flex>

                {/* Pricing display for GraphQL data */}
                {filteredAreaPriceObj?.length > 0 &&
                  selected !== null && (
                    <div>
                      <h3 style={{ marginBottom: '16px', color: '#391085' }}>Pricing Details</h3>
                      <div className="pitch-price">
                        {filteredAreaPriceObj[selected]?.price?.length > 0 ? (
                          <div>
                            {(() => {
                              // Group pricing by day
                              const groupedPricing: { [key: string]: any[] } = {};
                              filteredAreaPriceObj[selected].price.forEach((item: any) => {
                                if (!groupedPricing[item.day]) {
                                  groupedPricing[item.day] = [];
                                }
                                groupedPricing[item.day].push(item);
                              });

                              // Sort time slots within each day by start time
                              Object.keys(groupedPricing).forEach(day => {
                                groupedPricing[day].sort((a, b) => {
                                  // Convert time to minutes for comparison
                                  const timeToMinutes = (timeStr: string) => {
                                    const [time, period] = timeStr.split(' ');
                                    const [hours, minutes] = time.split(':').map(Number);
                                    let totalMinutes = hours * 60 + minutes;
                                    
                                    // Adjust for PM times
                                    if (period === 'PM' && hours !== 12) {
                                      totalMinutes += 12 * 60;
                                    }
                                    // Adjust for AM times (12 AM = 0 minutes)
                                    if (period === 'AM' && hours === 12) {
                                      totalMinutes = minutes;
                                    }
                                    
                                    return totalMinutes;
                                  };
                                  
                                  return timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
                                });
                              });

                              return Object.entries(groupedPricing).map(([day, items]) => (
                                <div key={day} style={{ 
                                  marginBottom: '16px'
                                }}>
                                  <strong style={{ 
                                    color: '#391085', 
                                    fontSize: '16px',
                                    display: 'block',
                                    marginBottom: '8px'
                                  }}>
                                    {day}:
                                  </strong>
                                  {items.length === 1 ? (
                                    // Single time slot for the day
                                    <div style={{ 
                                      marginLeft: '8px',
                                      color: '#495057',
                                      fontSize: '14px'
                                    }}>
                                      • {items[0].is24x7 ? "All Day" : `${items[0].startTime} - ${items[0].endTime}`}: 
                                      <span style={{ fontWeight: 'bold', color: '#000000' }}> ₹{items[0].price}/hr</span>
                                    </div>
                                  ) : (
                                    // Multiple time slots for the day
                                    <div style={{ marginLeft: '8px' }}>
                                      {items.map((item, index) => (
                                        <div key={index} style={{ 
                                          marginBottom: '6px',
                                          color: '#495057',
                                          fontSize: '14px'
                                        }}>
                                          • {item.is24x7 ? "All Day" : `${item.startTime} - ${item.endTime}`}: 
                                          <span style={{ fontWeight: 'bold', color: '#000000' }}> ₹{item.price}/hr</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ));
                            })()}
                          </div>
                        ) : (
                          <p>No pricing available for this pitch</p>
                        )}
                      </div>
                    </div>
                  )}
                </>
                )}
              </Flex>
            </Col>

            <Col md={8} sm={12} xs={12}>
              <Flex vertical gap={8} className="flex-cards">
                <Title level={3}>Cafe</Title>
                <Text>
                  {hasCafe ? (
                    <>
                      Certainly! <strong>{turf.name}</strong> features a
                      cafeteria that offers delectable meals, enhancing the
                      overall experience of your game.
                    </>
                  ) : (
                    <span>
                      <strong>{turf.name}</strong> doesn't have a cafe yet.
                    </span>
                  )}
                </Text>
              </Flex>
            </Col>
            <Col md={8} sm={12} xs={12}>
              <Flex vertical gap={8} className="flex-cards">
                <Title level={3}>Contact</Title>
                <div>
                  {// (turf?.data?.partnership
                  //   ? turf?.data?.displayContact
                  //   : turf?.data?.actualContact
                  // )
                  turf?.data?.actualContact
                    ?.split("/")
                    .map((item: string, index: number) => {
                      return (
                        <Flex gap={4} key={index} align="center">
                          <PhoneIcon size={18} color="#391085" />
                          <Link href={`tel:${item}`}>{item}</Link>
                        </Flex>
                      );
                    })}
                </div>
              </Flex>
            </Col>
          </Row>

          <Divider />
          <Flex vertical align="center" gap={12}>
            <MapComponent
              latitude={turf?.data?.location?.latitude}
              longitude={turf?.data?.location?.longitude}
            />
            <Text type="secondary">{turf?.data?.address}</Text>
          </Flex>
        </Flex>
      ),
    },
    {
      key: "facilities",
      label: <Title level={5}>Facilities</Title>,
      children: (
        <Flex vertical gap={12}>
          <Title level={2}>Facilities</Title>
          <Row gutter={[2, 2]}>
            <Col xs={24}>
              <Title level={4}>
                Some of the many facilities available are:
              </Title>
            </Col>
            <Col span={24}>
              <ul
                style={{
                  listStylePosition: "inside",
                }}
              >
                {turf?.data?.facilities ? (
                  <>
                    {turf.data.facilities.map(
                      (facility: string, index: number) => (
                        <li key={index}>{facility}</li>
                      )
                    )}
                  </>
                ) : (
                  <>
                    <li>Great ambience </li>
                    <li> {filteredAreaPriceObj.length} Turf(s) </li>
                    {isRainProtected && <li> Rain covered </li>}
                    {hasCafe && <li> Cafeteria present </li>}
                    <li> Provides playing equipments </li>
                  </>
                )}
              </ul>
            </Col>
          </Row>
        </Flex>
      ),
    },
    {
      key: "gallery",
      label: <Title level={5}>Gallery</Title>,
      children: (
        <Flex vertical gap={12}>
          <Title level={2}>Gallery</Title>
          <Row justify={"space-between"} gutter={[16, 16]}>
            {getGalleryImages().map((item) => {
              return (
                <Col md={8} sm={12} xs={24} key={item}>
                  <Image
                    src={item}
                    alt={imgMeta}
                    title={imgMeta}
                    key={item}
                    height={250}
                    width={"100%"}
                    style={{
                      borderRadius: "1rem",
                      objectFit: "cover",
                    }}
                  />
                </Col>
              );
            })}
            {getGalleryImages().length === 0 && (
              <Col xs={24}>
                <Text type="secondary">No images available for this sport.</Text>
              </Col>
            )}
          </Row>
        </Flex>
      ),
    },
    // {
    //   key: "offers",
    //   label: <Title level={5}>Offers</Title>,
    //   children: (
    //     <Flex vertical gap={12}>
    //       <Title level={2}>Offers</Title>
    //       {turf.data.webOffers && turf.data.webOffers.map((offer: string, index: number) => (
    //         <Image
    //           key={index}
    //           src={offer}
    //           alt={`Offer ${index + 1}`}
    //           title={`Offer ${index + 1}`}
    //           height={250}
    //           width={"100%"}
    //           style={{
    //             borderRadius: "1rem",
    //             // objectFit: "cover",
    //           }}
    //         />
    //       ))}
    //     </Flex>
    //   ),
    // },
  ];

  // Don't render if turf is not found
  // if (turfNotFound) {
  //   return null;
  // }

  // Show loading state while data is being fetched
  if (isDataLoading) {
    return (
      <div className="loader-container">
        <ClipLoader size={50} color="purple" loading={true} />
        <p>Please wait...</p>
      </div>
    );
  }

  // Don't render if turf is not found
  if (turfNotFound) {
    return null;
  }

  return (
    <>
      {turf?.fields?.length > 0 && seoInput && (
        <>
          <HelmetComponent
            title={generateTurfPageTitle(seoInput)}
            description={generateTurfMetaDescription(seoInput)}
            link={getTurfCanonicalUrl(seoInput)}
            image={seoInput.imageUrl}
            type="website"
            keywords={generateTurfKeywords(seoInput)}
            structuredData={generateTurfStructuredData(seoInput)}
          />
          <Head>
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(generateTurfBreadcrumbSchema(seoInput)) }}
            />
          </Head>
          <div className="single-tile-container">
            <Row justify={"center"}>
              <Button
                className="back-button"
                onClick={goBack}
                icon={<ArrowLeftIcon size={18} color="#391085" />}
                shape="circle"
                type="primary"
              />
              <Row className="header-row" align={"stretch"} justify={"center"}>
                <Col
                  md={8}
                  sm={24}
                  style={{
                    width: "100%",
                  }}
                >
                  <Image
                    src={
                      getGalleryImages()[0] || turf?.bannerImageUrl || turf?.imageUrl[0]
                    }
                    alt={imgMeta}
                    title={imgMeta}
                    width={"100%"}
                    className="banner-image"
                    preview={false}
                  />
                </Col>
                <Col
                  md={16}
                  sm={24}
                  style={{
                    backgroundColor: "rgba(57, 16, 133, 0.05)",
                  }}
                >
                  <div className="banner-content-parent">
                    <Row className="banner-content" gutter={[0, 48]}>
                      <Col xs={24}>
                        <Space direction="vertical">
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <Title className="turf-title">{turf?.name}</Title>
                            <ShareButton
                              className="share-btn"
                              city={city}
                              turfUsername={name || ''}
                              pitchName={selectedPitchName || pitchname || ""}
                              areaName={routeAreaName}
                            />
                          </div>
                          <Title level={4}>{turf?.data?.address} </Title>
                          <Title level={4}>
                            {`Starting From: ₹${matchedUsernamePrice || price}/hr`}{" "}
                          </Title>
                          {/* Sport Selector Tabs */}
                          {supportedSports.length > 1 && (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'nowrap', marginTop: '8px', overflowX: 'auto', maxWidth: '100%', paddingBottom: '4px' }}>
                              {supportedSports.map((sport: string) => (
                                <Button
                                  key={sport}
                                  type={selectedSport === sport ? 'primary' : 'default'}
                                  shape="round"
                                  size="middle"
                                  onClick={() => setSelectedSport(sport)}
                                  style={{
                                    fontWeight: selectedSport === sport ? 600 : 400,
                                    flexShrink: 0,
                                  }}
                                >
                                  {formatSportLabel(sport)}
                                  {!isSportEnabledAtTurf(sport, turf?.data?.enabledSports)
                                    ? ' · Closed'
                                    : ''}
                                </Button>
                              ))}
                            </div>
                          )}
                        </Space>
                      </Col>
                      <Row className="detail-menu-row">
                        <Col xs={0} md={24} className="banner-content-col2">
                          <Row>
                            <Col xs={12}>
                              {showBookNowDynamic(turf.data.city, cities) && (
                                <Button
                                  size="large"
                                  type="primary"
                                  shape="round"
                                  onClick={handleBookNow}
                                  disabled={isSelectedSportClosed}
                                  style={{
                                    width: "fit-content",
                                    fontWeight: 500,
                                  }}
                                >
                                  {isSelectedSportClosed ? 'Currently Closed' : 'Continue'}
                                </Button>
                              )}
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Row>
                  </div>
                </Col>
              </Row>

              <Col
                sm={24}
                md={18}
                lg={16}
                style={{
                  padding: "20px",
                }}
              >
                <Tabs defaultActiveKey="overview" items={items} />
              </Col>
            </Row>

            <Col xs={24} md={0} className="book-now-btn-container">
              <Row
                align={"middle"}
                style={{
                  padding: "1rem",
                }}
              >
                <Col xs={12}>
                  <Flex vertical gap={8}>
                    <Title level={3}>Starting From</Title>
                    <Text>₹{matchedUsernamePrice || price}/hr</Text> {/* Display the price here */}
                  </Flex>
                </Col>
                {showBookNowDynamic(turf.data.city, cities) && (
                  <Col xs={12}>
                    <Button
                      type="primary"
                      shape="round"
                      size="large"
                      block={true}
                      onClick={handleBookNow}
                      disabled={isSelectedSportClosed}
                    >
                      {isSelectedSportClosed ? 'Currently Closed' : 'Continue'}
                    </Button>
                  </Col>
                )}
              </Row>
            </Col>
          </div>
        </>
      )}
    </>
  );
}

export default SingleTileContainer;
